import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import DateTimePicker from "../../components/DateTimePicker";
import s from "./Announcements.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

const AUDIENCE_PRESETS = [
  ["ALL",      "Everyone"],
  ["STUDENTS", "All students"],
  ["PARENTS",  "All parents"],
  ["STAFF",    "All staff"],
  ["ROLE:Teacher",       "Teachers only"],
  ["ROLE:Janitor",       "Janitors only"],
  ["ROLE:SecurityGuard", "Security only"],
];

function fmtDate(s) {
  if (!s) return "";
  return new Date(s).toLocaleString();
}

/** Friendly "5m ago / 2h ago / 3d ago / Apr 12" rendering for the card
 *  footer. Mirrors the topbar bell's idiom so timestamps read the same
 *  way everywhere in the app. */
function fmtRelative(iso) {
  if (!iso) return "";
  const ms  = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return fmtDate(iso);
  const min = Math.floor(ms / 60000);
  if (min < 1)  return "just now";
  if (min < 60) return `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24) return `${hr}h ago`;
  const d   = Math.floor(hr / 24);
  if (d   < 7)  return `${d}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { month: "short", day: "numeric" });
  } catch { return fmtDate(iso); }
}

/** Audience "tier" — drives the card's left accent stripe, the avatar
 *  icon, and the pill colour. Keeps the per-token visual language
 *  consistent across the page so a parent can spot "this is for my kid's
 *  grade" vs "this is school-wide" in a single glance. */
function audienceTier(tokens) {
  if (!tokens || tokens.length === 0) return "all";
  if (tokens.includes("ALL")) return "all";
  if (tokens.some(t => t.startsWith("ROLE:"))) return "role";
  if (tokens.some(t => t.startsWith("CLASSOFFERING:"))) return "class";
  if (tokens.some(t => t.startsWith("SECTION:") || t.startsWith("GRADELEVEL:"))) return "cohort";
  if (tokens.some(t => t === "PARENTS" || t === "STUDENTS" || t === "STAFF")) return "group";
  return "all";
}

/** Bucket a postedAt timestamp into a recency group so the feed can
 *  surface section headers ("Today" / "This week" / "Earlier"). */
function recencyBucket(iso) {
  if (!iso) return "earlier";
  const now = new Date();
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "earlier";
  const sameDay = then.getFullYear() === now.getFullYear()
               && then.getMonth() === now.getMonth()
               && then.getDate() === now.getDate();
  if (sameDay) return "today";
  const ms = now.getTime() - then.getTime();
  if (ms >= 0 && ms < 7 * 24 * 60 * 60 * 1000) return "week";
  return "earlier";
}

function parseTokens(a) {
  if (!a) return [];
  return a.split(",").map(t => t.trim()).filter(Boolean);
}

function tokenLabel(token, { sectionsById, gradeLevelsById, offeringsById, subjectsById }) {
  if (token.startsWith("SECTION:")) {
    const id = Number(token.substring(8));
    return `Section · ${sectionsById[id]?.name || `#${id}`}`;
  }
  if (token.startsWith("GRADELEVEL:")) {
    const id = Number(token.substring(11));
    return `Grade · ${gradeLevelsById[id]?.name || `#${id}`}`;
  }
  if (token.startsWith("CLASSOFFERING:")) {
    const id = Number(token.substring(14));
    const co = offeringsById[id];
    if (!co) return `Class · #${id}`;
    const subj = subjectsById[co.subjectId]?.title || subjectsById[co.subjectId]?.code || `Subject #${co.subjectId}`;
    const sec  = sectionsById[co.sectionId]?.name || `Section #${co.sectionId}`;
    return `${subj} · ${sec}`;
  }
  if (token.startsWith("ROLE:")) return token.substring(5) + " role";
  return AUDIENCE_PRESETS.find(([k]) => k === token)?.[1] || token;
}

export default function AnnouncementsPage() {
  const user = getUser();
  const role = user?.role;
  const isAdminLike = role === "Admin" || role === "SuperAdmin";
  const isTeacher   = role === "Teacher";
  const canPost     = isAdminLike || isTeacher;

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);

  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(null);

  const [sections, setSections]       = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [subjects, setSubjects]       = useState([]);
  const [myOfferings, setMyOfferings] = useState([]); // teacher only

  const sectionsById    = useMemo(() => Object.fromEntries(sections.map(r => [r.id, r])), [sections]);
  const gradeLevelsById = useMemo(() => Object.fromEntries(gradeLevels.map(r => [r.id, r])), [gradeLevels]);
  const subjectsById    = useMemo(() => Object.fromEntries(subjects.map(r => [r.id, r])), [subjects]);
  const offeringsById   = useMemo(() => Object.fromEntries(myOfferings.map(r => [r.id, r])), [myOfferings]);

  const labelCtx = { sectionsById, gradeLevelsById, offeringsById, subjectsById };

  // Group teacher's offerings by subject for the picker.
  const offeringsBySubject = useMemo(() => {
    const map = new Map();
    for (const o of myOfferings) {
      if (!map.has(o.subjectId)) map.set(o.subjectId, []);
      map.get(o.subjectId).push(o);
    }
    return Array.from(map.entries()).map(([subjectId, list]) => ({
      subjectId,
      subject: subjectsById[subjectId],
      offerings: list,
    }));
  }, [myOfferings, subjectsById]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role]);

  // Sections + grade levels are fetched for EVERY role now — not just
  // posters — so the per-card audience pill renders "Grade 7" instead of
  // the raw "GRADELEVEL:5" token for parents / students / teachers too.
  // Subjects stay gated behind canPost because only posters need them
  // (for the class-offering picker in the form).
  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []).catch(() => []),
      canPost
        ? fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []).catch(() => [])
        : Promise.resolve([]),
    ]).then(([se, gl, sj]) => {
      setSections(se || []);
      setGradeLevels(gl || []);
      setSubjects(sj || []);
    }).catch(() => {});
  }, [canPost]);

  useEffect(() => {
    if (!isTeacher || !user?.linkedEntityId) return;
    fetch(`${API_BASE}/api/class-offerings?teacherStaffId=${user.linkedEntityId}`)
      .then(r => r.json())
      .then(list => setMyOfferings(list || []))
      .catch(() => setMyOfferings([]));
    // eslint-disable-next-line
  }, [isTeacher, user?.linkedEntityId]);

  // ── Parent: children + their current section/grade, for the optional
  //    per-child filter chip strip. Only runs for Parent users with a
  //    linked entity id. Each child becomes a chip; selecting one
  //    narrows the feed CLIENT-side (the backend already returned the
  //    union of everything any of the parent's children can see).
  const isParent = role === "Parent";
  const [parentChildren, setParentChildren] = useState([]);   // [{ id, firstName, lastName, currentSectionId, gradeLevelId, gradeLabel }]
  const [childFilter,    setChildFilter]    = useState("all"); // "all" | <childId>

  useEffect(() => {
    if (!isParent || !user?.linkedEntityId) return;
    let cancelled = false;
    (async () => {
      try {
        const links = await fetch(`${API_BASE}/api/parent-student?parentId=${user.linkedEntityId}`)
          .then(r => r.ok ? r.json() : []).catch(() => []);
        const kids = await Promise.all(
          (Array.isArray(links) ? links : []).map(l =>
            fetch(`${API_BASE}/api/students/${l.studentId}`)
              .then(r => r.ok ? r.json() : null).catch(() => null)
          )
        );
        if (cancelled) return;
        setParentChildren(kids.filter(Boolean));
      } catch { /* leave empty — chips just won't render */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [isParent, user?.linkedEntityId]);

  async function load() {
    setLoading(true); setError("");
    try {
      const url = isAdminLike
        ? `${API_BASE}/api/announcements`
        : `${API_BASE}/api/announcements?audience=auto`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to load");
      setItems(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  function newBlank() {
    return {
      title: "",
      body: "",
      audience: isTeacher ? "" : "ALL",
      expiresAt: "",
    };
  }

  function startCreate() { setEditing("new"); setForm(newBlank()); setError(""); }
  function startEdit(row) {
    setEditing(row);
    setForm({
      title: row.title,
      body: row.body,
      audience: row.audience || (isTeacher ? "" : "ALL"),
      expiresAt: row.expiresAt ? row.expiresAt.substring(0, 16) : "",
    });
    setError("");
  }
  function cancel() { setEditing(null); setForm(null); setError(""); }

  // ── Teacher picker helpers ──
  const selectedOfferingIds = useMemo(() => {
    if (!form) return new Set();
    return new Set(
      parseTokens(form.audience)
        .filter(t => t.startsWith("CLASSOFFERING:"))
        .map(t => Number(t.substring(14)))
        .filter(n => !Number.isNaN(n))
    );
  }, [form]);

  function setOfferingIds(ids) {
    const tokens = Array.from(ids).map(id => `CLASSOFFERING:${id}`);
    setForm(p => ({ ...p, audience: tokens.join(",") }));
  }
  function toggleOffering(id) {
    const next = new Set(selectedOfferingIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setOfferingIds(next);
  }
  function selectAllOfferings() {
    setOfferingIds(new Set(myOfferings.map(o => o.id)));
  }
  function clearOfferings() {
    setOfferingIds(new Set());
  }
  function toggleSubject(subjectId) {
    const inSubject = myOfferings.filter(o => o.subjectId === subjectId).map(o => o.id);
    const next = new Set(selectedOfferingIds);
    const allOn = inSubject.every(id => next.has(id));
    if (allOn) inSubject.forEach(id => next.delete(id));
    else       inSubject.forEach(id => next.add(id));
    setOfferingIds(next);
  }

  async function save(e) {
    e.preventDefault();
    if (isTeacher && selectedOfferingIds.size === 0) {
      setError("Pick at least one class to post to.");
      return;
    }
    setBusy(true); setError("");
    try {
      const isNew = editing === "new";
      const url = isNew
        ? `${API_BASE}/api/announcements`
        : `${API_BASE}/api/announcements/${editing.id}`;
      const payload = {
        ...form,
        expiresAt: form.expiresAt ? form.expiresAt + ":00" : null,
      };
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      await load();
      cancel();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function remove(row) {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/announcements/${row.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const canManage = (row) =>
    isAdminLike || (isTeacher && row.postedByUsername === user?.username);

  // ── Filtered + grouped feed (memoised) ──
  // 1. Apply the per-child filter (parent only). For "all" or non-parent,
  //    pass-through. For a selected child, narrow to items whose audience
  //    matches that child's grade / section (or is school-wide).
  // 2. Bucket by recency so the rendered feed can show "Today" / "This
  //    week" / "Earlier" section headers without re-walking the list.
  const filteredItems = useMemo(() => {
    if (!isParent || childFilter === "all") return items;
    const child = parentChildren.find(c => String(c.id) === String(childFilter));
    if (!child) return items;
    const childSec   = child.currentSectionId;
    const childGrade = childSec ? sectionsById[childSec]?.gradeLevelId : null;
    return items.filter(it => {
      const tokens = parseTokens(it.audience);
      if (tokens.length === 0) return true; // no audience defined = visible to all
      for (const t of tokens) {
        // Broadcast-to-anyone tokens apply to every selected child.
        // STUDENTS is included so a "Math quiz tomorrow" notice still
        // shows when the parent has narrowed to a specific child —
        // student-facing posts apply to all of the parent's children
        // by definition.
        if (t === "ALL" || t === "PARENTS" || t === "STUDENTS") return true;
        if (childSec   != null && t === `SECTION:${childSec}`)     return true;
        if (childGrade != null && t === `GRADELEVEL:${childGrade}`) return true;
      }
      return false;
    });
  }, [items, isParent, childFilter, parentChildren, sectionsById]);

  const recencyGroups = useMemo(() => {
    const today = [], week = [], earlier = [];
    for (const it of filteredItems) {
      const b = recencyBucket(it.postedAt);
      if (b === "today")   today.push(it);
      else if (b === "week") week.push(it);
      else                 earlier.push(it);
    }
    return [
      { id: "today",   label: "Today",     items: today },
      { id: "week",    label: "This week", items: week },
      { id: "earlier", label: "Earlier",   items: earlier },
    ];
  }, [filteredItems]);

  /** Display label for one child chip — "Maria · Grade 7" when possible,
   *  falls back to just the first name when section/grade can't resolve. */
  function childChipLabel(c) {
    const name = (c.firstName || c.lastName || `Student #${c.id}`).trim();
    const sec  = c.currentSectionId ? sectionsById[c.currentSectionId] : null;
    const grade = sec ? gradeLevelsById[sec.gradeLevelId]?.name : null;
    return grade ? `${name} · ${grade}` : name;
  }

  return (
    <div className={s.page}>
      {/* Premium hero — gold accent, megaphone icon, animated entrance.
          Replaces the plain h1 strip the page used to lead with. */}
      <div className={s.anHero}>
        <div className={s.anHeroIcon} aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11l18-5v12L3 14v-3z"/>
            <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
          </svg>
        </div>
        <div className={s.anHeroBody}>
          <div className={s.eyebrow}>Announcements</div>
          <h1 className={s.anHeroTitle}>What's New</h1>
          <p className={s.anHeroSub}>
            {isAdminLike
              ? "All school announcements. Post, edit and delete here."
              : canPost
                ? "Posts you've made and ones you're authorized to see."
                : isParent && parentChildren.length > 0
                  ? `Posts for ${parentChildren.length === 1 ? "your child" : `your ${parentChildren.length} children`} — school-wide updates, student bulletins, and posts targeting their grade or section. Quizzes, substitute teachers, events — anything a parent should know to help their child prep.`
                  : "Announcements relevant to you."}
          </p>
        </div>
        {canPost && !editing && (
          <button className={s.btnPrimary} onClick={startCreate}>+ New post</button>
        )}
      </div>

      {error && <div className={s.error}>{error}</div>}

      {/* Parent child-filter chips — only when the parent has 2+ linked
          children. Pure client-side narrow on top of what the backend
          already returned (which is the union of all their children's
          allowed posts). */}
      {isParent && parentChildren.length > 1 && (
        <div className={s.anChildFilter} role="tablist" aria-label="Filter by child">
          <span className={s.anChildLabel}>Filter</span>
          <div className={s.anChildChips}>
            <button
              type="button"
              role="tab"
              aria-selected={childFilter === "all"}
              className={`${s.anChildChip} ${childFilter === "all" ? s.anChildChipActive : ""}`}
              onClick={() => setChildFilter("all")}>
              All children
              <span className={s.anChildChipCount}>{parentChildren.length}</span>
            </button>
            {parentChildren.map(c => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={String(childFilter) === String(c.id)}
                className={`${s.anChildChip} ${String(childFilter) === String(c.id) ? s.anChildChipActive : ""}`}
                onClick={() => setChildFilter(c.id)}>
                {childChipLabel(c)}
              </button>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className={s.card}>
          <h2 className={s.cardTitle}>{editing === "new" ? "New announcement" : "Edit announcement"}</h2>
          <form onSubmit={save} className={s.form}>
            <div className={s.fieldFull}>
              <label className={s.label}>Title <span className={s.req}>*</span></label>
              <input
                className={s.input}
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                required
              />
            </div>
            <div className={s.fieldFull}>
              <label className={s.label}>Body <span className={s.req}>*</span></label>
              <textarea
                className={s.textarea}
                rows={5}
                value={form.body}
                onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                required
              />
            </div>

            {isTeacher ? (
              <div className={s.fieldFull}>
                <label className={s.label}>Post to <span className={s.req}>*</span></label>
                {offeringsBySubject.length === 0 ? (
                  <div className={s.offeringEmpty}>
                    You aren't assigned to any class offerings yet. Ask an admin to link you to your classes.
                  </div>
                ) : (
                  <div className={s.offeringPicker}>
                    {offeringsBySubject.map(({ subjectId, subject, offerings }) => {
                      const allOn = offerings.every(o => selectedOfferingIds.has(o.id));
                      const label = subject?.title || subject?.code || `Subject #${subjectId}`;
                      return (
                        <div key={subjectId} className={s.offeringGroup}>
                          <div className={s.offeringGroupHead}>
                            <span>{label}</span>
                            <button type="button" className={s.pickerShortcut} onClick={() => toggleSubject(subjectId)}>
                              {allOn ? `Clear ${label}` : `All ${label}`}
                            </button>
                          </div>
                          {offerings.map(o => (
                            <label key={o.id} className={s.offeringRow}>
                              <input
                                type="checkbox"
                                checked={selectedOfferingIds.has(o.id)}
                                onChange={() => toggleOffering(o.id)}
                              />
                              <span>{sectionsById[o.sectionId]?.name || `Section #${o.sectionId}`}</span>
                              {o.schedule && <span style={{ color: "#aaa", fontSize: "0.78rem" }}>· {o.schedule}</span>}
                            </label>
                          ))}
                        </div>
                      );
                    })}
                    <div className={s.pickerFooter}>
                      <span className={s.pickerSelectedCount}>
                        {selectedOfferingIds.size} of {myOfferings.length} selected
                      </span>
                      <div>
                        <button type="button" className={s.btnGhost} onClick={clearOfferings}>Clear</button>
                        <button type="button" className={s.pickerShortcut} onClick={selectAllOfferings}>All my classes</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={s.field}>
                <label className={s.label}>Audience</label>
                <select
                  className={s.input}
                  value={form.audience || "ALL"}
                  onChange={e => setForm(p => ({ ...p, audience: e.target.value }))}>
                  <optgroup label="Broad">
                    {AUDIENCE_PRESETS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                  </optgroup>
                  <optgroup label="By section">
                    {sections.map(se => (
                      <option key={`s${se.id}`} value={`SECTION:${se.id}`}>Section · {se.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="By grade level">
                    {gradeLevels.map(g => (
                      <option key={`g${g.id}`} value={`GRADELEVEL:${g.id}`}>Grade · {g.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            )}

            <div className={s.field}>
              <label className={s.label}>Expires at (optional)</label>
              {/* Premium DateTimePicker — same "YYYY-MM-DDTHH:MM" value
                  contract as the native datetime-local it replaces, so
                  the save handler (which appends ":00" before POST)
                  keeps working byte-for-byte. */}
              <DateTimePicker
                value={form.expiresAt}
                onChange={v => setForm(p => ({ ...p, expiresAt: v }))}
                placeholder="No expiry — leave blank"
              />
            </div>
            <div className={s.formActions}>
              <button type="button" className={s.btnGhost} onClick={cancel}>Cancel</button>
              <button type="submit" className={s.btnPrimary} disabled={busy}>
                {busy ? "Saving…" : (editing === "new" ? "Post" : "Save changes")}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading && (
        <div className={s.anSkel}>
          <div className={s.anSkelRow} />
          <div className={s.anSkelRow} />
          <div className={s.anSkelRow} />
        </div>
      )}

      {!loading && filteredItems.length === 0 && (
        <>
          <div className={s.anEmpty}>
            <div className={s.anEmptyIcon} aria-hidden="true">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.8"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div className={s.anEmptyTitle}>
              {items.length === 0
                ? "No announcements right now"
                : "No announcements match this filter"}
            </div>
            <div className={s.anEmptySub}>
              {items.length === 0
                ? (isParent
                    ? "School-wide updates, student bulletins (quizzes, substitutes, events), and posts targeting your children's grades will appear here as soon as they're published."
                    : "New posts will appear here as soon as they're published.")
                : <>Try <button type="button" className={s.anEmptyLink}
                                onClick={() => setChildFilter("all")}>All children</button> to see every post you have access to.</>}
            </div>
          </div>

          {/* Diagnostic scope panel — only for parents on a genuinely
              empty feed (not the "filter narrowed it to zero" case). Spells
              out exactly which audiences match this parent so they can
              tell at a glance whether nothing's been posted yet vs their
              account isn't linked to any students yet. */}
          {isParent && items.length === 0 && (
            <div className={s.anScope}>
              <div className={s.anScopeHead}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round"
                     aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8"  x2="12" y2="8.01"/>
                </svg>
                <strong>What you can see here</strong>
              </div>

              <ul className={s.anScopeList}>
                <li className={s.anScopeOk}>
                  <span className={`${s.anPill} ${s.anPill_all}`}>School-wide</span>
                  Posts to <strong>everyone</strong>
                </li>
                <li className={s.anScopeOk}>
                  <span className={`${s.anPill} ${s.anPill_group}`}>All parents</span>
                  Posts addressed to <strong>parents</strong>
                </li>
                <li className={s.anScopeOk}>
                  <span className={`${s.anPill} ${s.anPill_group}`}>All students</span>
                  Posts to <strong>students</strong> (quizzes, substitutes, events)
                </li>

                {parentChildren.length === 0 ? (
                  <li className={s.anScopeWarn}>
                    <span className={`${s.anPill} ${s.anPill_role}`}>Action needed</span>
                    Your account isn't linked to any students yet — ask the
                    registrar to link you to your child's record. Once
                    linked, you'll also see posts targeting their grade
                    and section.
                  </li>
                ) : (
                  parentChildren.map(c => {
                    const sec   = c.currentSectionId ? sectionsById[c.currentSectionId]  : null;
                    const grade = sec ? gradeLevelsById[sec.gradeLevelId] : null;
                    const childName = (c.firstName || c.lastName || `Student #${c.id}`).trim();
                    if (!sec) {
                      return (
                        <li key={c.id} className={s.anScopeWarn}>
                          <span className={`${s.anPill} ${s.anPill_role}`}>Action needed</span>
                          <strong>{childName}</strong> isn't enrolled in a section yet
                          — ask the registrar. Grade/section posts can't be
                          targeted until enrolment lands.
                        </li>
                      );
                    }
                    return (
                      <li key={c.id} className={s.anScopeOk}>
                        <span className={`${s.anPill} ${s.anPill_cohort}`}>
                          {grade?.name ? `${grade.name} · ${sec.name}` : sec.name}
                        </span>
                        Posts targeting <strong>{childName}</strong>'s grade,
                        section, and class offerings
                      </li>
                    );
                  })
                )}
              </ul>

              <div className={s.anScopeFoot}>
                If a teacher posted something internal (staff-only, faculty-only,
                or for another grade) it won't appear here by design — those
                are scoped to staff communications.
              </div>
            </div>
          )}
        </>
      )}

      {!loading && filteredItems.length > 0 && (
        <div className={s.feed}>
          {recencyGroups.map(g => g.items.length === 0 ? null : (
            <section key={g.id} className={s.anGroup}>
              <header className={s.anGroupHead}>
                <h3 className={s.anGroupTitle}>{g.label}</h3>
                <span className={s.anGroupCount}>
                  {g.items.length} post{g.items.length === 1 ? "" : "s"}
                </span>
              </header>
              <div className={s.anGroupItems}>
                {g.items.map((a, idx) => {
                  const tokens = parseTokens(a.audience);
                  const tier   = audienceTier(tokens);
                  return (
                    <article
                      key={a.id}
                      className={`${s.anPost} ${s["anTier_" + tier]}`}
                      style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}>
                      <header className={s.anPostHead}>
                        <div className={s.anPostIcon} aria-hidden="true">
                          {tierIcon(tier)}
                        </div>
                        <div className={s.anPostIdent}>
                          <h3 className={s.anPostTitle}>{a.title}</h3>
                          <div className={s.anPostMeta}>
                            <span title={fmtDate(a.postedAt)}>{fmtRelative(a.postedAt)}</span>
                            {a.postedByUsername && (
                              <span className={s.anPostMetaSep}>·</span>
                            )}
                            {a.postedByUsername && (
                              <span>by <b>{a.postedByUsername}</b></span>
                            )}
                            {a.expiresAt && (
                              <>
                                <span className={s.anPostMetaSep}>·</span>
                                <span className={s.anExpires}
                                      title={`expires ${fmtDate(a.expiresAt)}`}>
                                  expires {fmtRelative(a.expiresAt)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {canManage(a) && (
                          <div className={s.anPostActions}>
                            <button type="button" className={s.btnGhost}
                                    onClick={() => startEdit(a)}>Edit</button>
                            <button type="button" className={s.btnDanger}
                                    onClick={() => remove(a)}>Delete</button>
                          </div>
                        )}
                      </header>

                      <div className={s.anPostBody}>{a.body}</div>

                      <div className={s.anPostAudience}>
                        {/* "Posted for" pills — each audience token rendered
                            as a tone-coloured chip so a parent can tell at
                            a glance whether a post is school-wide, for
                            their child's grade, or for a specific class. */}
                        {tokens.length === 0 ? (
                          <span className={`${s.anPill} ${s.anPill_all}`}>
                            School-wide
                          </span>
                        ) : (
                          tokens.map((t, i) => (
                            <span key={i}
                                  className={`${s.anPill} ${s["anPill_" + tokenTone(t)]}`}>
                              {tokenLabel(t, labelCtx)}
                            </span>
                          ))
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Per-tier icon for the card avatar tile. */
function tierIcon(tier) {
  const stroke = { stroke: "currentColor", strokeWidth: 1.9,
                   strokeLinecap: "round", strokeLinejoin: "round", fill: "none" };
  if (tier === "all") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
        <path d="M3 11l18-5v12L3 14v-3z"/>
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
      </svg>
    );
  }
  if (tier === "group") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    );
  }
  if (tier === "cohort") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
      </svg>
    );
  }
  if (tier === "class") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
      </svg>
    );
  }
  // role
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" {...stroke}>
      <circle cx="12" cy="8" r="4"/>
      <path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2"/>
    </svg>
  );
}

/** Per-token pill tone. Drives the per-pill colour so a viewer can spot
 *  "School-wide" (blue) vs "Grade 7" (gold) vs "Math 10 · Faithfulness"
 *  (purple) without reading every label. */
function tokenTone(t) {
  if (t === "ALL") return "all";
  if (t === "PARENTS" || t === "STUDENTS" || t === "STAFF") return "group";
  if (t.startsWith("ROLE:")) return "role";
  if (t.startsWith("GRADELEVEL:") || t.startsWith("SECTION:")) return "cohort";
  if (t.startsWith("CLASSOFFERING:")) return "class";
  return "all";
}
